import React, { useState, useEffect, useRef } from 'react';
import { getRestaurant, listenToPublicUser } from '../services/DataStore';
import { Restaurant } from '../models/Restaurant';
import RestaurantView from './RestaurantView';
import { useAuth } from '../contexts/AuthContext';

const RestaurantList = ({ restaurantIds, title, favoriteRestaurants = [], onClose }) => {
  const { currentUser } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRestaurant, setHoveredRestaurant] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const hideTimeoutRef = useRef(null);
  const listRef = useRef(null);

  // Set up listener for public user data
  useEffect(() => {
    let publicUserUnsubscribe = null;

    if (currentUser?.uid) {
      console.log('Setting up public user listener for RestaurantList:', currentUser.uid);
      publicUserUnsubscribe = listenToPublicUser(currentUser.uid, (updatedPublicUser) => {
        console.log('RestaurantList received public user update:', updatedPublicUser);
        if (updatedPublicUser) {
          // Reload restaurants when public user data changes
          loadRestaurants();
        }
      });
    }

    return () => {
      if (publicUserUnsubscribe) {
        console.log('Cleaning up public user listener in RestaurantList');
        publicUserUnsubscribe();
      }
    };
  }, [currentUser?.uid]);

  const loadRestaurants = async () => {
    setLoading(true);
    try {
      const restaurantPromises = restaurantIds.slice(0, 10).map(id => getRestaurant(id));
      const loadedRestaurants = await Promise.all(restaurantPromises);
      setRestaurants(loadedRestaurants.filter(r => r !== null && r instanceof Restaurant));
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load of restaurants
  useEffect(() => {
    loadRestaurants();
  }, [restaurantIds, favoriteRestaurants]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside both the list and the popup
      const isOutsideList = listRef.current && !listRef.current.contains(event.target);
      const popup = document.querySelector('.restaurant-popup');
      const isOutsidePopup = !popup || !popup.contains(event.target);
      
      if (isOutsideList && isOutsidePopup) {
        console.log('Click outside detected, closing list');
        onClose?.();
      }
    };

    // Use mousedown instead of click for better touch support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  const handleMouseEnter = (restaurant, event) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const listRect = listRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    let x = listRect.right - 400;
    let y = rect.top;
    
    if (spaceBelow < 300 && spaceAbove > 300) {
      y = rect.bottom - 300;
    }
    
    setPopupPosition({ x, y });
    setHoveredRestaurant(restaurant);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredRestaurant(null);
    }, 300);
  };

  const handleShowMap = (restaurant) => {
    if (restaurant.location?.coordinates) {
      const { latitude, longitude } = restaurant.location.coordinates;
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="overflow-y-auto h-[calc(100vh-200px)]">
        <div className="divide-y divide-gray-200">
          {restaurants.map(restaurant => (
            <div 
              key={restaurant.id} 
              className="p-4 hover:bg-[#EBE4D2]"
              onMouseEnter={(e) => handleMouseEnter(restaurant, e)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex flex-col">
                {/* First line: Yelp burst and restaurant name */}
                <div className="flex items-center space-x-2">
                  <img 
                    src="/images/yelp_burst.svg"
                    alt="Yelp"
                    className="w-6 h-6"
                  />
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {restaurant.name}
                  </h3>
                  <span className="text-accent font-semibold ml-auto">
                    {restaurant.price}
                  </span>
                </div>
                
                {/* Second line: Rating and review count */}
                <div className="mt-1 flex items-center space-x-2">
                  <div className="flex items-center">
                    <img 
                      src={Restaurant.getRatingImage(restaurant.rating)}
                      alt={`${restaurant.rating} stars`}
                      className="h-4 w-auto"
                    />
                    <span className="ml-1 text-sm text-gray-600">
                      ({restaurant.reviewCount})
                    </span>
                    {favoriteRestaurants.includes(restaurant.id) && (
                      <span className="material-symbols-outlined ml-1 text-yellow-500 text-sm">
                        star
                      </span>
                    )}
                  </div>
                  {restaurant.categories && restaurant.categories.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {restaurant.categories[0].title}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hover Popup */}
      {hoveredRestaurant && (
        <div 
          className="fixed z-50 w-[400px] h-[400px] restaurant-popup"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`
          }}
          onMouseEnter={() => {
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleMouseLeave}
        >
          <RestaurantView 
            restaurant={hoveredRestaurant}
            onShowMap={handleShowMap}
          />
        </div>
      )}
    </div>
  );
};

export default RestaurantList; 